import { useEffect } from 'react';
import { useSimStore } from '../store/useSimStore';
import { buildPayload, applyPayload } from './syncPayload';

const CHANNEL_NAME = 'anesthesia-sim-sync';

/**
 * Hook for same-device sync via BroadcastChannel.
 *
 * The payload shape and receiver apply-logic live in syncPayload.js, shared
 * with the two-device WebSocket transport (useWebSocketSync) so the two can
 * never drift apart.
 *
 * Critical design rule: do NOT call useSimStore() (the React hook) inside this
 * effect or include it in deps. The hook returns a new object reference on every
 * state change, which would tear down and rebuild the BroadcastChannel on every
 * update — causing the very message that triggered the rebuild to be dropped.
 *
 * Instead:
 *   - Publisher: useSimStore.subscribe(fn) — static, fires on future changes only
 *   - Subscriber: useSimStore.getState() inside the handler — reads store once at call time
 */
export function useBroadcastSync(isPublisher = false) {
  useEffect(() => {
    let channel;
    try {
      channel = new BroadcastChannel(CHANNEL_NAME);
    } catch (error) {
      console.error('BroadcastChannel not supported:', error);
      useSimStore.getState().setIsConnected(false);
      return;
    }

    if (isPublisher) {
      // Push the current snapshot immediately on mount. Without this, a freshly
      // (re)loaded instructor only broadcasts on the NEXT state change — so
      // refreshing the instructor (which resets the store to defaults) would
      // leave an already-open monitor showing the stale pre-refresh state until
      // a control is next touched. This initial post propagates the reset.
      channel.postMessage({ type: 'STATE_UPDATE', payload: buildPayload(useSimStore.getState()) });

      // Subscribe to the Zustand store outside React's render cycle.
      // Fires on every state change and pushes the full snapshot to the channel.
      const unsubscribe = useSimStore.subscribe((state) => {
        channel.postMessage({ type: 'STATE_UPDATE', payload: buildPayload(state) });
      });

      return () => {
        unsubscribe();
        channel.close();
      };
    } else {
      // Monitor display: receive state from instructor and apply it.
      const handleMessage = (event) => {
        const { type, payload } = event.data;
        if (type !== 'STATE_UPDATE') return;
        applyPayload(payload);
      };

      channel.addEventListener('message', handleMessage);

      return () => {
        channel.removeEventListener('message', handleMessage);
        channel.close();
      };
    }
  }, [isPublisher]); // isPublisher is stable; no store ref in deps
}
